import { TrashIcon, UploadIcon } from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import { useEffect, useRef } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import AutoExpand from 'react-expanding-textarea';
import { match } from 'ts-pattern';

import ColorTint from 'components/ColorTint';
import { ControlButton } from 'components/ContentBlock';
import { ContentBlockItem } from 'components/ContentBlockItem';
import {
  ContentBlockItemImage,
  useContentItemImageContext,
} from 'components/ContentBlockItemImage';
import DarkMode from 'components/DarkMode';
import Box from 'components/base/Box';
import Button from 'components/base/Button';
import Text from 'components/base/Text';
import { STORY_COPY } from 'copy/story';
import { EditorMachineContext } from 'machines/editor';

import { ApiStoryGroupSize } from 'gql/api/types-api.generated';
import useFileUploadS3 from 'hooks/mutations/server/use-file-upload-s3';
import useLoadedImage from 'hooks/use-loaded-image';
import report from 'lib/report';
import { figcaptionSchema } from 'schemas/story';
import { bytesToMb, mbToBytes, noop } from 'utils/helpers';
import { getDpr, optimizeAsset } from 'utils/imgix';
import {
  groupSizeToWidthMap,
  isStoryImageEmpty,
  isStoryImagePopulated,
  mapImageAttributesToImageAst,
} from 'utils/story/story';

import { AssetMimeType } from 'types/Assets';
import { LayoutChangeFn, StoryImage, StoryImageBlockProps } from 'types/Story';

import { StoryContentBlock } from './StoryContentBlock';
import { ContentLayoutToolbar, StoryToolbarPositioner } from './StoryToolbar';

const MAX_COLUMNS = 2;

const mapStoryImageToBlockProps = (
  storyImage: StoryImage
): StoryImageBlockProps => {
  return match<StoryImage, StoryImageBlockProps>(storyImage)
    .when(isStoryImagePopulated, (item) => {
      const [figureNode] = item.content.ast.content;
      const [imgNode, figcaptionNode] = figureNode.content;

      const captionText = figcaptionNode ? figcaptionNode.content[0].text : '';

      return {
        state: 'POPULATED',
        id: item.id,
        base64Preview: item.content.base64Preview,
        img: {
          src: optimizeAsset(imgNode.attr.src, {
            'max-w': groupSizeToWidthMap['LARGE'],
            'max-h': groupSizeToWidthMap['LARGE'],
            dpr: getDpr(),
            fit: 'fill',
            q: 70,
          }),
          alt: captionText,
        },
        caption: captionText,
      };
    })
    .when(isStoryImageEmpty, (item) => {
      return {
        state: 'EMPTY',
        id: item.id,
        caption: item.content.caption,
      };
    })
    .exhaustive();
};

const mapBlockPropsToStoryImage = (props: StoryImageBlockProps): StoryImage => {
  return match<StoryImageBlockProps, StoryImage>(props)
    .with({ state: 'EMPTY' }, (item) => {
      return {
        type: 'IMAGE',
        id: item.id,
        placeholder: null,
        content: {
          state: 'EMPTY',
          caption: item.caption,
        },
      };
    })
    .with({ state: 'POPULATED' }, (item) => {
      return {
        type: 'IMAGE',
        state: 'EMPTY',
        id: item.id,
        placeholder: null,
        content: {
          state: 'POPULATED',
          base64Preview: item.base64Preview,
          ast: mapImageAttributesToImageAst({
            src: item.img.src,
            caption: item.caption,
          }),
        },
      };
    })
    .exhaustive();
};

type StoryImageGroupProps = {
  items: StoryImage[];
  groupId: string;
  onUpdateItem(block: StoryImage): void;
  onRemoveItem(id: string): void;
  onAddItem(): void;
  onRemoveGroup(): void;
  onLayoutChange: LayoutChangeFn;
  layout: ApiStoryGroupSize;
};

export function StoryImageGroup(props: StoryImageGroupProps) {
  const focusedGroupId = EditorMachineContext.useSelector(
    (state) => state.context.focusedGroupId
  );

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedGroupId === props.groupId && ref.current) {
      ref.current.focus();
    }
  }, [focusedGroupId, props.groupId]);

  const getControls = () => {
    if (props.items.length < MAX_COLUMNS) {
      return {
        controls: 'ADD',
        onAdd: props.onAddItem,
        canAdd: true,
      } as const;
    } else {
      return {
        controls: 'NONE',
      } as const;
    }
  };

  /**
   * should technically not be a renderable state
   */
  if (props.items.length === 0) {
    return null;
  }

  const mappedStoryImageBlocks = props.items.map(mapStoryImageToBlockProps);

  return (
    <StoryContentBlock
      ref={ref}
      // TODO (storytelling): add support for focused state
      isFocused={focusedGroupId === props.groupId}
      onRemove={props.onRemoveGroup}
      {...getControls()}
    >
      <StoryGroupGrid
        style={{
          gridTemplateColumns: `repeat(${props.items.length}, 1fr)`,
        }}
      >
        {mappedStoryImageBlocks.map((node) => {
          return (
            <ContentBlockItemImage.Provider
              key={node.id}
              item={node}
              context={props.items.length === 1 ? 'SINGLE' : 'MULTI'}
              updateItem={(item) => {
                props.onUpdateItem(mapBlockPropsToStoryImage(item));
              }}
              removeItem={() => {
                props.onRemoveItem(node.id);
              }}
            >
              {match(node)
                .with({ state: 'POPULATED' }, (node) => {
                  return (
                    <ContentBlockItem.Root>
                      <DarkMode>
                        <ContentBlockItem.Controls>
                          {props.items.length > 1 && (
                            <ControlButton
                              variant="blur"
                              icon="standalone"
                              className={ContentBlockItem.BUTTON_CLASSNAME}
                              onClick={() => {
                                props.onRemoveItem(node.id);
                              }}
                            >
                              <TrashIcon />
                            </ControlButton>
                          )}
                        </ContentBlockItem.Controls>
                      </DarkMode>
                      <StoryImageBlockContent />
                    </ContentBlockItem.Root>
                  );
                })
                .with({ state: 'EMPTY' }, () => {
                  return (
                    <ContentBlockItem.Root>
                      <StoryImageItemControls />
                      <StoryImageFigure>
                        <StoryImageBlockEmpty />
                      </StoryImageFigure>
                    </ContentBlockItem.Root>
                  );
                })
                .exhaustive()}
            </ContentBlockItemImage.Provider>
          );
        })}
        <StoryToolbarPositioner>
          <ContentLayoutToolbar
            onLayoutChange={props.onLayoutChange}
            layout={props.layout}
          />
        </StoryToolbarPositioner>
      </StoryGroupGrid>
    </StoryContentBlock>
  );
}

function StoryImageItemControls() {
  const contentItemContext = useContentItemImageContext();

  if (
    contentItemContext.state === 'UPLOADING' ||
    contentItemContext.context === 'SINGLE'
  ) {
    return null;
  }

  return (
    <ContentBlockItem.Controls>
      <ControlButton
        variant="ghost"
        className={ContentBlockItem.BUTTON_CLASSNAME}
        icon="standalone"
        onClick={() => {
          contentItemContext.removeItem();
        }}
      >
        <TrashIcon />
      </ControlButton>
    </ContentBlockItem.Controls>
  );
}

const SUPPORTED_ASSET_TYPES: AssetMimeType[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/svg+xml',
];

const MAX_SIZE_IN_BYTES = mbToBytes(50);

export function StoryImageBlockEmpty() {
  const fileUploadS3 = useFileUploadS3();

  const contentItemContext = useContentItemImageContext();

  const item = contentItemContext.item;

  const dropzone = useDropzone({
    accept: SUPPORTED_ASSET_TYPES,
    maxSize: MAX_SIZE_IN_BYTES,
    onDropAccepted: async ([file]) => {
      if (!file) return;

      contentItemContext.setItemState('UPLOADING');

      await fileUploadS3
        .mutateAsync({ file, bucket: 'worlds' })
        .then((upload) => contentItemContext.populateItem({ item, upload }))
        .catch((err) => report('Failed to upload file to S3', err));
    },
    onDropRejected: ([err]) => {
      if (!err) return;
      /**
       * TODO (storytelling): add error handling (e.g. toast message, etc.
       */
    },
  });

  if (fileUploadS3.meta.previewBase64) {
    return (
      <StoryImageFigure>
        <div style={{ position: 'relative' }}>
          <StoryImageAsset src={fileUploadS3.meta.previewBase64.src} alt="" />
          <LoadingProgress
            percentProgress={Math.round(fileUploadS3.meta.percentProgress)}
          />
        </div>
        <FigcaptionEditable caption={item.caption} onChange={noop} />
      </StoryImageFigure>
    );
  }

  return (
    <>
      <StoryImageBlockEmptyContainer {...dropzone.getRootProps()}>
        {dropzone.fileRejections[0] && (
          <FileUploadError
            fileRejection={dropzone.fileRejections[0]}
            maxSizeInBytes={MAX_SIZE_IN_BYTES}
          />
        )}
        <input {...dropzone.getInputProps()} id="test" />
        <StoryImageBlockEmptyContainerDetails>
          <StoryImageBlockEmptyIconWrapper>
            <UploadIcon />
          </StoryImageBlockEmptyIconWrapper>

          <Text size={1} weight="medium" css={{ marginBottom: '$1' }}>
            Add image
          </Text>
          {/* TODO: configurable via props? */}
          <Text size={0} weight="regular" color="dim">
            JPG, PNG or SVG. 50MB max
          </Text>
        </StoryImageBlockEmptyContainerDetails>
      </StoryImageBlockEmptyContainer>
      <FigcaptionEditable
        caption={item.caption}
        onChange={(caption) => {
          contentItemContext.updateItem({
            ...item,
            caption,
          });
        }}
      />
    </>
  );
}

function FigcaptionEditable(props: {
  onChange: (caption: string) => void;
  caption: string;
}) {
  return (
    // TODO: format breaks when you type in a caption
    <StoryImageFigcaption>
      <UnstyledTextarea
        placeholder={STORY_COPY.CAPTION_PLACEHOLDER}
        maxLength={200}
        value={props.caption}
        onChange={(ev) => {
          const parsedCaption = figcaptionSchema.safeParse(ev.target.value);
          if (parsedCaption.success) {
            props.onChange(parsedCaption.data);
          }
        }}
      />
    </StoryImageFigcaption>
  );
}

function StoryImageBlockContent() {
  const fileUploadS3 = useFileUploadS3();

  const contentItemContext = useContentItemImageContext();

  const item = contentItemContext.item;

  const loadedImage = useLoadedImage(
    item.state === 'POPULATED' ? { type: 'raster', src: item.img.src } : null
  );

  const dropzone = useDropzone({
    accept: SUPPORTED_ASSET_TYPES,
    maxSize: MAX_SIZE_IN_BYTES,
    onDropAccepted: async ([file]) => {
      if (!file) return;

      contentItemContext.setItemState('UPLOADING');

      await fileUploadS3
        .mutateAsync({ file, bucket: 'worlds' })
        .then((upload) => contentItemContext.populateItem({ item, upload }))
        .catch((err) => report('Failed to upload file to S3', err));
    },
    onDropRejected: ([err]) => {
      if (!err) return;
      /**
       * TODO (storytelling): add error handling (e.g. toast message, etc.
       */
    },
  });

  const getLoadedImage = () => {
    // is not a reachable state
    if (item.state === 'EMPTY') return null;

    const previewImage = fileUploadS3.meta.previewBase64 || item.base64Preview;

    if (contentItemContext.state === 'INITIAL' && loadedImage) {
      return <StoryImageAsset src={loadedImage.src} alt={item.caption} />;
    }

    if (previewImage) {
      return <StoryImageAsset src={previewImage.src} alt={item.caption} />;
    }

    // TODO: add placeholder
    return null;
  };

  return (
    <StoryImageFigure>
      <div
        {...dropzone.getRootProps()}
        style={{
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        {dropzone.fileRejections[0] && (
          <FileUploadError
            fileRejection={dropzone.fileRejections[0]}
            maxSizeInBytes={MAX_SIZE_IN_BYTES}
          />
        )}
        {contentItemContext.state === 'UPLOADING' && (
          <LoadingProgress
            percentProgress={Math.round(fileUploadS3.meta.percentProgress)}
          />
        )}
        <input {...dropzone.getInputProps()} id="test" />
        {getLoadedImage()}
      </div>

      <FigcaptionEditable
        caption={item.caption}
        onChange={(caption) => {
          contentItemContext.updateItem({
            ...item,
            caption,
          });
        }}
      />
    </StoryImageFigure>
  );
}

export function StoryImageBlock(props: { item: StoryImage }) {
  const mappedProps = mapStoryImageToBlockProps(props.item);

  if (mappedProps.state === 'EMPTY') {
    return null;
  }

  return (
    <StoryImageFigure>
      <StoryImageAsset src={mappedProps.img.src} alt={mappedProps.caption} />
      {mappedProps.caption && (
        <StoryImageFigcaption size={{ '@initial': 0, '@bp0': 1 }}>
          {mappedProps.caption}
        </StoryImageFigcaption>
      )}
    </StoryImageFigure>
  );
}

function LoadingProgress(props: { percentProgress: number }) {
  return (
    <Box
      css={{
        position: 'absolute',
        left: '$4',
        bottom: '$4',
      }}
    >
      <Button.Loading size={0} variant="blur">
        {Math.round(props.percentProgress)}%
      </Button.Loading>
    </Box>
  );
}

function FileUploadError(props: {
  fileRejection: FileRejection;
  maxSizeInBytes: number;
}) {
  return props.fileRejection.errors.map((error, index) => {
    if (error.code === 'file-too-large') {
      return (
        <ErrorContainer key={index} tint="red">
          File size is too large. Must be {bytesToMb(props.maxSizeInBytes)}MB or
          less
        </ErrorContainer>
      );
    }
    return (
      <ErrorContainer key={index} tint="red">
        {error.message}
      </ErrorContainer>
    );
  });
}

const ErrorContainer = styled(ColorTint, {
  paddingY: '$2',
  paddingX: '$4',
  position: 'absolute',
  bottom: '$6',
  left: '50%',
  transform: 'translateX(-50%)',
  borderRadius: '$2',
});

const UnstyledTextarea = styled(AutoExpand, 'textarea', {
  outline: 'none',
  border: 'none',
  padding: 0,
  backgroundColor: 'transparent',
  resize: 'none',
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  color: 'inherit',
  height: 'auto',
  overflow: 'auto',
});

const StoryImageAsset = styled('img', {
  maxWidth: '100%',
  display: 'block',
});

export const StoryGroupGrid = styled('div', {
  display: 'grid',
  gap: '$7',
});

const StoryImageFigcaption = styled(Text, 'figcaption', {
  outline: 'none',

  '@bp0': {
    size: 1,
  },
});

StoryImageFigcaption.defaultProps = {
  color: 'dim',
  size: { '@initial': 0, '@bp1': 1 },
};

const StoryImageFigure = styled('figure', {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '$2',
});

const StoryImageBlockEmptyIconWrapper = styled('div', {
  height: '$avatar5',
  width: '$avatar5',
  backgroundColor: '$black5',
  borderRadius: '$round',
  padding: '$4',
  marginBottom: '$2',
  transition: 'background-color $1 $ease',
});

const StoryImageBlockEmptyContainer = styled('div', {
  backgroundColor: '$black3',
  flex: 1,
  position: 'relative',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingY: '$7',
  aspectRatio: '4 / 3',

  '&[role="button"]': {
    cursor: 'pointer',
  },

  '@hover': {
    '&:hover': {
      [`${StoryImageBlockEmptyIconWrapper}`]: {
        backgroundColor: '$black10',
      },
    },
  },
});

const StoryImageBlockEmptyContainerDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});
